import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Trash2 } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ScrollableDropdown, Spinner ,SearchableDropdown,} from "../../../Components/Common";
import { useProductMaster } from "../Hooks/useProductMaster";
import * as routes from "../../../routes";

const CreateProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId } = useParams(); // productId
  const isEditMode = location.pathname.includes(routes.EDIT);

  const {
    loading,
    createProductData,
    editProductData,
    fetchProductById,
    fetchCategories,
    fetchAttributes,
    getUOMs,
  } = useProductMaster();

  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [uom, setUOM] = useState("")
  const [selectedAttributes, setSelectedAttributes] = useState([]);

  const [activeDropdown, setActiveDropdown] = useState(null);

  const categoryRef = useRef(null);
  const nameRef = useRef(null);
  const valueRef = useRef(null);

  const [loadingProduct, setLoadingProduct] = useState(false);

  const [initialValues, setInitialValues] = useState({
    product_name: "",
    specification: "",
    make: "",
    SKU: "",
    UOM: "",
    category_id: "",
    attributeName: "",
    attributeValue: "",
  });

  const fetchAllCategories = async () => {
    const data = await fetchCategories({ limit: 0 });
    if (data) setCategories(data);
  };

  const fetchAllAttributes = async () => {
    const data = await fetchAttributes({ limit: 0 });
    if (data) setAttributes(data);
  };

  const fetchProduct = async () => {
    setLoadingProduct(true);
    const data = await fetchProductById(instanceId);
    if (data) {
      setInitialValues({
        product_name: data.product_name,
        specification: data.specification,
        make: data.make,
        SKU: data.SKU,
        UOM: uom?.id,
        category_id: data.category_id || "",
        attributeName: "",
        attributeValue: "",
      });
      setSelectedAttributes(data.attributes || []);
    }
    setLoadingProduct(false);
  };

  const validationSchema = Yup.object({
    product_name: Yup.string().required("Product Name is required"),
    specification: Yup.string().required("Specification is required"),
    category_id: Yup.string().required("Category is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const payload = {
      product_name: values.product_name,
      specification: values.specification,
      make: values.make,
      SKU: values.SKU,
      UOM_id: uom?.id,
      category_id: values.category_id,
      attributes_input: selectedAttributes.map((attr) => ({
        name: attr.name,
        value: attr.value,
      })),
    };

    if (isEditMode && instanceId) {
      const updated = await editProductData(instanceId, payload);
      if (updated) navigate(-1);
    } else {
      const created = await createProductData(payload);
      if (created) navigate(-1);
    }
    setSubmitting(false);
  };

  const handleNameOnChange = (val, setFieldValue) => {
    setFieldValue("attributeName", val);
    setFieldValue("attributeValue", "");
  };

  const handleValueOnChange = (val, values, setFieldValue) => {
    setFieldValue("attributeValue", val);

    if (val && values.attributeName) {
      const exists = selectedAttributes.some(
        (attr) => attr.name === values.attributeName && attr.value === val
      );

      if (!exists) {
        setSelectedAttributes([
          ...selectedAttributes,
          { name: values.attributeName, value: val },
        ]);
      }

      setFieldValue("attributeValue", "");
      setFieldValue("attributeName", "");
    }
  };

  const handleOnDeleteAttribute = (index) => {
    setSelectedAttributes([
      ...selectedAttributes.slice(0, index),
      ...selectedAttributes.slice(index + 1),
    ]);
  };

   useEffect(() => {
    fetchAllCategories();
    fetchAllAttributes();
  }, []);

  useEffect(() => {
    if (isEditMode && instanceId) {
      fetchProduct();
    }
  }, [isEditMode, instanceId]);

  return (
    <div className="mx-2 my-1 md:my-2">
      {/* Header */}
      <div className="flex items-center gap-4 mx-2 mt-3 md:mt-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-primary text-center text-sm font-lg"
        >
          <IoMdArrowRoundBack size={25} className="text-primary" />
        </button>
        <h1 className="text-xl md:text-2xl text-secondary font-semibold">
          {isEditMode ? "Edit Product" : "Create Product"}
        </h1>
      </div>

      {/* Form container */}
      <div className="flex justify-center">
        {loadingProduct ? (
          <div className="flex items-center justify-center h-[50vh]">
            <Spinner />
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnChange
            validateOnBlur={false}
            enableReinitialize
          >
            {({ values, setFieldValue }) => (
              <Form className="w-full md:w-[90%] mx-auto">
                <div className="grid md:grid-cols-2 gap-2 md:gap-4 rounded-md p-4 pt-2 bg-white">
                  {/* Product Information Section */}
                  <div className="space-y-2 border-b md:border-r md:border-b-0 border-gray-200 pb-4 md:pr-6">
                    <h2 className="text-lg font-semibold text-gray-700 w-full text-center">
                      Product Information
                    </h2>

                    {/* Product Name */}
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Product Name <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="product_name"
                        type="text"
                        placeholder="Enter product name"
                        className="border-b border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200"
                      />
                      <ErrorMessage
                        name="product_name"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </div>

                    {/* Specification */}
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Specification{" "}
                        <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="specification"
                        type="text"
                        placeholder="Enter specification"
                        className="border-b border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200"
                      />
                      <ErrorMessage
                        name="specification"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                      </div>
                      
                      {/* make */}
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Brand{" "}
                          <sub className="text-red-500 top-0">*</sub>
                        </label>
                        <Field
                          name="make"
                          type="text"
                          placeholder="Enter make"
                          className="border-b border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200"
                        />
                        <ErrorMessage
                          name="make"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>
                      </div>
                      {/* SKU + UOM */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Stock Keeping Unit{" "}
                          <sub className="text-red-500 top-0">*</sub>
                        </label>
                        <Field
                          name="SKU"
                          type="text"
                          disabled
                          placeholder="Enter SKU"
                          className="border-b border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200 bg-gray-100 cursor-not-allowed
"
                        />
                        <ErrorMessage
                          name="SKU"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Unit of Measurement{" "}
                          <sub className="text-red-500">*</sub>
                        </label>
                          <SearchableDropdown
                            fetchFunction={getUOMs}
                            onSelect={setUOM}
                            value={uom}
                            labelKey="name"
                            placeholder="Select UOM"
                            width="84"
                          />
                        </div>
                      </div>

                    {/* Category */}
                    <div className="flex flex-col gap-1 relative">
                      <label className="text-sm font-medium text-gray-700">
                        Category <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field name="category_id">
                        {({ field, form }) => (
                          <ScrollableDropdown
                            ref={categoryRef}
                            label="Select Category"
                            options={categories.map((c) => ({
                              label: c.name,
                              value: c.id,
                            }))}
                            value={field.value || ""}
                            onChange={(val) =>
                              form.setFieldValue("category_id", val)
                            }
                            isOpen={activeDropdown === "category"}
                            onToggle={() =>
                              setActiveDropdown((prev) =>
                                prev === "category" ? null : "category"
                              )
                            }
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="category_id"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </div>
                  </div>

                  {/* Product Attributes Section */}
                  <div className="space-y-4 md:pl-4">
                    <h2 className="text-lg font-semibold text-gray-700 md:mb-2 w-full text-center">
                      Product Attributes
                    </h2>

                    <div className="grid grid-cols-2 gap-6 m-0 md:mt-2">
                      {/* Name Dropdown */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Name
                        </label>
                        <ScrollableDropdown
                          ref={nameRef}
                          label="Select Attribute Name"
                          options={[
                            ...new Set(attributes.map((a) => a.name)),
                          ].map((name) => ({
                            label: name,
                            value: name,
                          }))}
                          value={values.attributeName || ""}
                          onChange={(val) =>
                            handleNameOnChange(val, setFieldValue)
                          }
                          isOpen={activeDropdown === "attributeName"}
                          onToggle={() =>
                            setActiveDropdown((prev) =>
                              prev === "attributeName" ? null : "attributeName"
                            )
                          }
                        />
                        <ErrorMessage
                          name="attributeName"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>

                      {/* Value Dropdown */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Value
                        </label>
                        <ScrollableDropdown
                          ref={valueRef}
                          label="Select Attribute Value"
                          options={attributes
                            .filter((a) => a.name === values.attributeName)
                            .map((a) => ({ label: a.value, value: a.value }))}
                          value={values.attributeValue || ""}
                          onChange={(val) =>
                            handleValueOnChange(val, values, setFieldValue)
                          }
                          isOpen={activeDropdown === "attributeValue"}
                          onToggle={() =>
                            setActiveDropdown((prev) =>
                              prev === "attributeValue"
                                ? null
                                : "attributeValue"
                            )
                          }
                        />
                        <ErrorMessage
                          name="attributeValue"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <span className="text-sm font-medium text-gray-700">
                        Attributes Table
                      </span>
                      <div className="mt-1 border rounded-lg shadow-sm overflow-hidden bg-white max-h-[25vh] md:max-h-[36vh] overflow-y-auto">
                        <table className="w-full text-sm text-center border-collapse">
                          <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                              <th className="px-4 py-2 text-gray-700 font-medium border-b">
                                Name
                              </th>
                              <th className="px-4 py-2 text-gray-700 font-medium border-b">
                                Value
                              </th>
                              <th className="px-4 py-2 text-gray-700 font-medium border-b">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedAttributes.length > 0 ? (
                              selectedAttributes.map((attr, index) => (
                                <tr
                                  key={index}
                                  className=" hover:bg-gray-50 transition-colors"
                                >
                                  <td className="px-4 py-2 text-gray-800 font-normal">
                                    {attr.name}
                                  </td>
                                  <td className="px-4 py-2 text-gray-800 font-normal">
                                    {attr.value}
                                  </td>
                                  <td className="px-4 py-2 flex justify-center items-center">
                                    <Trash2
                                      size={17}
                                      className="text-red-500 hover:underline text-sm cursor-pointer"
                                      onClick={() =>
                                        handleOnDeleteAttribute(index)
                                      }
                                    />
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="px-4 py-2 text-gray-400 italic"
                                >
                                  No attributes present.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-2 border-gray-200 mt-1">
                  <button
                    type="submit"
                    className="px-5 py-2 bg-primary text-white flex justify-center shadow hover:shadow-lg hover:bg-secondary active:scale-95 transition-all text-sm font-medium"
                    disabled={loading || loadingProduct}
                  >
                    {loading ? <Spinner /> : "Save Product"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default CreateProduct;
